import React, { ReactChild, useMemo } from 'react';

import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { FormItem, Tooltip } from '@bringg/react-components';
import classNames from 'classnames';
import { Rule } from 'antd/lib/form';
import { NamePath } from 'antd/lib/form/interface';
import { useTranslation } from 'react-i18next';

import styles from './form-element.module.scss';

interface Props {
	name: NamePath;
	children: ReactChild;
	label?: string;
	required?: boolean;
	rules?: Rule[];
	formItemClassName?: string;
	tooltipText?: string;
	valuePropName?: string;
	getTooltipPopupContainer?: (trigger: HTMLElement) => HTMLElement;
	labelSuffix?: ReactChild;
	labelClassName?: string;
}

const gitTriggerParentNode = (trigger: HTMLElement) => trigger.parentNode as HTMLElement;

export const FormElement = ({
	label,
	name,
	children,
	rules,
	required,
	formItemClassName,
	tooltipText,
	valuePropName,
	getTooltipPopupContainer = gitTriggerParentNode,
	labelSuffix,
	labelClassName
}: Props) => {
	const { t } = useTranslation();

	const inputRules = useMemo<Rule[]>(() => {
		const baseRules: Rule[] = [];

		if (required) {
			baseRules.push({ required, message: t('GLOBAL.FIELD_REQUIRED') });
			baseRules.push({
				validator: async (_, value: string) => {
					if (value && typeof value === 'string' && !value?.trim()) {
						return Promise.reject(t('GLOBAL.FIELD_REQUIRED'));
					}

					return Promise.resolve();
				}
			});
		}

		return rules?.length ? baseRules.concat(rules) : baseRules;
	}, [t, rules, required]);

	return (
		<>
			{label ? (
				<label
					className={classNames(styles.inputLabel, labelClassName, {
						[styles.inputLabelRequired]: required
					})}
				>
					{label}
					{tooltipText ? (
						<Tooltip
							title={tooltipText}
							placement="right"
							getPopupContainer={getTooltipPopupContainer}
							overlayClassName={styles.formElementTooltip}
						>
							<BringgIcon iconName={BringgFontIcons.Info} className={styles.tooltipIcon} />
						</Tooltip>
					) : null}
					{labelSuffix}
				</label>
			) : null}
			<FormItem
				name={name}
				rules={inputRules}
				className={classNames(styles.formItemInput, formItemClassName)}
				valuePropName={valuePropName}
			>
				{children}
			</FormItem>
		</>
	);
};
